import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileText, Lock, Unlock, Sparkles, Save, ClipboardCheck, Pencil, HeartHandshake } from "lucide-react";
import { api, BACKEND_URL, getAdminToken } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

const STATUSES = [
  "started", "incomplete", "submitted", "under_objective_review",
  "waiting_for_documents", "waiting_for_reference_responses",
  "qualified_for_interview", "interview_scheduled", "selected",
  "waitlisted", "not_selected", "withdrawn", "closed",
];

const MARKS = [
  ["meets", "Meets"],
  ["does_not_meet", "Does not meet"],
  ["needs_documentation", "Needs documentation"],
  ["not_applicable", "Not applicable"],
];
const MARK_LABELS = Object.fromEntries(MARKS);

const OUTCOME_LABELS = {
  meets: "Meets objective criteria",
  needs_documentation: "Needs documentation",
  does_not_meet: "Does not meet objective criteria",
};
const OUTCOME_COLORS = {
  meets: "#2A6E4F",
  needs_documentation: "#8A6D1D",
  does_not_meet: "#8A2A2A",
};

export default function ApplicationDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [meta, setMeta] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [status, setStatus] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/applications/${id}`);
    setDoc(data);
    setStatus(data.status);
    setReasonCode(data.decision_reason_code || "");
    setReason(data.decision_reason || "");
    if (data.ai_summary) setSummary(data.ai_summary);
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.get("/admin/reason-codes").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/criteria").then((r) => setCriteria(r.data.criteria || [])).catch(() => {});
  }, []);

  if (!doc) return <div className="p-8" style={{ color: "#475569" }}>Loading…</div>;
  const data = doc.data || {};
  const contact = data.contact || {};
  const income = data.income || {};
  const storyLocked = !doc.story_unlocked_for_admin;
  const review = doc.objective_review || null;
  const gatedCodes = meta?.status_reason_codes?.[status] || null;

  const saveStatus = async () => {
    if (gatedCodes && !reasonCode) {
      toast.error("Select a structured reason code for this status change.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/applications/${id}/status`, {
        status,
        reason_code: gatedCodes ? reasonCode : (reasonCode || null),
        written_reason: reason,
      });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/admin/applications/${id}/notes`, { note });
      setNote(""); toast.success("Note added"); load();
    } catch (e) { toast.error("Failed"); }
  };
  const genSummary = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/applications/${id}/ai-summary`, { params: { regenerate: true } });
      setSummary(data); toast.success("Summary generated");
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  const downloadPdf = async () => {
    const t = getAdminToken();
    const res = await fetch(`${BACKEND_URL}/api/admin/applications/${id}/pdf`, { headers: { Authorization: `Bearer ${t}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${doc.confirmation_number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  const downloadDetailCsv = async () => {
    const t = getAdminToken();
    const res = await fetch(`${BACKEND_URL}/api/admin/applications/${id}/export.csv`, { headers: { Authorization: `Bearer ${t}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${doc.confirmation_number}-detail.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const downloadFile = async (f) => {
    const t = getAdminToken();
    const res = await fetch(`${BACKEND_URL}/api/admin/applications/${id}/files/${f.id}`, { headers: { Authorization: `Bearer ${t}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = f.name; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm" style={{ color: "#475569" }}>
        <ArrowLeft className="w-4 h-4" /> Back to applications
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3 mt-3">
        <div>
          <h1 className="font-serif text-3xl" style={{ color: "#0B2D59" }}>{contact.legal_name || "(no name)"}</h1>
          <div className="text-sm" style={{ color: "#475569" }}>
            Confirmation # {doc.confirmation_number} · Submitted {doc.submitted_at ? new Date(doc.submitted_at).toLocaleString() : "—"}
            {doc.criteria_version_at_submit ? ` · Criteria v${doc.criteria_version_at_submit}` :
              doc.criteria_version_at_start ? ` · Criteria v${doc.criteria_version_at_start}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={doc.status} />
          <button className="p2p-btn-outline inline-flex items-center gap-2 text-sm" data-testid={ADMIN.downloadPdfBtn} onClick={downloadPdf} style={{ padding: "0.4rem 0.9rem" }}>
            <Download className="w-4 h-4" /> PDF
          </button>
          <button className="p2p-btn-outline inline-flex items-center gap-2 text-sm" data-testid="admin-detail-csv-btn" onClick={downloadDetailCsv} style={{ padding: "0.4rem 0.9rem" }}>
            <Download className="w-4 h-4" /> Detail CSV
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <div className="lg:col-span-2 space-y-5">
          <Block title="Contact"><Pairs obj={contact} /></Block>
          <Block title="Additional adult occupants">
            {(data.occupants || []).length === 0 ? <Empty>None</Empty> :
              (data.occupants || []).map((o, i) => <Pairs key={i} obj={o} />)}
          </Block>
          <Block title="Current housing"><Pairs obj={data.housing || {}} /></Block>
          <Block title="Income & ability to pay"><Pairs obj={income} /></Block>
          <Block title={`References (${(data.references || []).length})`}>
            {(data.references || []).map((r, i) => (
              <div key={i} className="border-b py-2" style={{ borderColor: "#E2EBF1" }}><Pairs obj={r} /></div>
            ))}
            {(data.references || []).length === 0 && <Empty>None</Empty>}
          </Block>
          <Block title="Documents">
            {(doc.documents || []).length === 0 ? <Empty>No documents uploaded.</Empty> : (
              <ul className="divide-y" style={{ borderColor: "#E2EBF1" }}>
                {doc.documents.map((f) => (
                  <li key={f.id} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4" style={{ color: "#0B2D59" }} />
                      <span style={{ color: "#0B2D59" }}>{f.name}</span>
                      <span className="p2p-chip">{f.category}</span>
                    </div>
                    <button onClick={() => downloadFile(f)} className="text-sm underline" style={{ color: "#0B2D59" }}>Download</button>
                  </li>
                ))}
              </ul>
            )}
          </Block>
          <Block title="Lease acknowledgments"><Pairs obj={data.lease_ack || {}} /></Block>
          <Block title="Signature"><Pairs obj={data.signature || {}} /></Block>

          <ObjectiveReviewCard appId={id} criteria={criteria} review={review} onSaved={load} />

          <StoryCard doc={doc} data={data} meta={meta} storyLocked={storyLocked} appId={id} onChanged={load} />

          {["selected", "interview_scheduled"].includes(doc.status) && (
            <SupportPlanCard appId={id} plan={doc.support_plan} onSaved={load} />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <div className="p2p-card p-5">
            <div className="font-serif text-lg mb-3" style={{ color: "#0B2D59" }}>Status</div>
            <select className="p2p-input" value={status}
              onChange={(e) => { setStatus(e.target.value); setReasonCode(""); }}
              data-testid={ADMIN.statusSelect}>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
            </select>
            {gatedCodes && (
              <>
                <div className="text-xs font-semibold uppercase tracking-wide mt-3" style={{ color: "#475569" }}>
                  Reason code (required)
                </div>
                <select className="p2p-input mt-1" value={reasonCode}
                  onChange={(e) => setReasonCode(e.target.value)}
                  data-testid={ADMIN.reasonCodeSelect}>
                  <option value="">Select a reason code…</option>
                  {gatedCodes.map((c) => (
                    <option key={c} value={c}>{meta.reason_codes[c] || c}</option>
                  ))}
                </select>
              </>
            )}
            <textarea className="p2p-input mt-2" rows={3}
              placeholder={gatedCodes ? "Optional note (attached to the reason code)" : "Optional note"}
              value={reason} onChange={(e) => setReason(e.target.value)} />
            <button className="p2p-btn-primary w-full mt-3 inline-flex items-center justify-center gap-2" data-testid={ADMIN.saveStatusBtn}
              onClick={saveStatus} disabled={busy}>
              <Save className="w-4 h-4" /> Save status
            </button>
            {doc.decision_reason_label && (
              <div className="text-xs mt-3" style={{ color: "#475569" }}>
                Current reason: <span style={{ color: "#0B2D59" }}>{doc.decision_reason_label}</span>
              </div>
            )}
          </div>

          <div className="p2p-card p-5">
            <div className="font-serif text-lg mb-2 flex items-center gap-2" style={{ color: "#0B2D59" }}>
              <Sparkles className="w-4 h-4" /> AI organizational summary
            </div>
            <p className="text-xs mb-2" style={{ color: "#475569" }}>
              Organizational only — never a rental decision. It does not score, rank, or judge anyone.
              Verify all information directly and apply the same written criteria to every applicant.
            </p>
            <button className="p2p-btn-outline w-full" data-testid={ADMIN.aiSummaryBtn} onClick={genSummary} disabled={busy}>
              {busy ? "Generating…" : summary ? "Regenerate" : "Generate summary"}
            </button>
            {summary && (
              <div className="mt-4 space-y-3 text-sm" style={{ color: "#0B2D59" }}>
                <SummaryRow label="Basic facts" v={summary.basic_facts} />
                <SummaryRow label="Stated housing goal" v={summary.stated_housing_goal} />
                <SummaryRow label="Rent payment plan" v={summary.rent_payment_plan} />
                <SummaryRow label="Property care plan" v={summary.property_care_plan} />
                <SummaryRow label="Communication & problem-solving" v={summary.communication_problem_solving} />
                <SummaryRow label="Applicant-stated strengths/supports — not scored and not used as a rental decision factor" v={summary.stated_strengths} />
                <SummaryRow label="Reference status" v={summary.reference_status} />
                <SummaryRow label="Story summary" v={summary.story_summary} />
                {summary.missing_or_unanswered?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase" style={{ color: "#B91C1C" }}>Documents / answers still needed</div>
                    <ul className="list-disc pl-5">{summary.missing_or_unanswered.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  </div>
                )}
                {summary.needs_verification?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase" style={{ color: "#0A5FB3" }}>Needs verification</div>
                    <ul className="list-disc pl-5">{summary.needs_verification.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  </div>
                )}
                {summary.inconsistencies_to_clarify?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase" style={{ color: "#8A6D1D" }}>Inconsistencies to clarify (neutral)</div>
                    <ul className="list-disc pl-5">{summary.inconsistencies_to_clarify.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  </div>
                )}
                {summary.suggested_interview_questions?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase" style={{ color: "#0B2D59" }}>Suggested neutral interview questions</div>
                    <ul className="list-disc pl-5">{summary.suggested_interview_questions.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="p2p-card p-5">
            <div className="font-serif text-lg mb-2" style={{ color: "#0B2D59" }}>Private notes</div>
            <textarea className="p2p-input" rows={3} placeholder="Add a private note…"
              data-testid={ADMIN.addNoteInput}
              value={note} onChange={(e) => setNote(e.target.value)} />
            <button className="p2p-btn-secondary w-full mt-2" data-testid={ADMIN.addNoteBtn} onClick={addNote}>Add note</button>
            <ul className="mt-3 space-y-2 text-sm">
              {(doc.admin_notes || []).map((n) => (
                <li key={n.id} className="p-2 rounded-lg" style={{ background: "#F4F8FB", border: "1px solid #E2EBF1" }}>
                  <div className="text-xs" style={{ color: "#475569" }}>{n.author} · {new Date(n.ts).toLocaleString()}</div>
                  <div style={{ color: "#0B2D59" }}>{n.text}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function ObjectiveReviewCard({ appId, criteria, review, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (review?.items) { setItems(review.items); setNote(review.note || ""); }
  }, [review]);

  const completed = !!review?.completed;
  const showForm = !completed || editing;
  const allMarked = criteria.length > 0 && criteria.every((c) => items[c.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/applications/${appId}/objective-review`, { items, note });
      toast.success("Objective review saved");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save review");
    } finally { setSaving(false); }
  };

  return (
    <div className="p2p-card p-5" data-testid={ADMIN.objectiveReview.card}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-serif text-lg flex items-center gap-2" style={{ color: "#0B2D59" }}>
          <ClipboardCheck className="w-4 h-4" /> Objective criteria review
        </div>
        {completed && !editing && (
          <button className="p2p-btn-outline text-sm inline-flex items-center gap-1" style={{ padding: "0.3rem 0.8rem" }}
            data-testid={ADMIN.objectiveReview.editBtn} onClick={() => setEditing(true)}>
            <Pencil className="w-3 h-3" /> Edit review
          </button>
        )}
      </div>
      <p className="text-sm mt-1" style={{ color: "#475569" }}>
        Mark each written criterion. The same criteria apply to every applicant. This review must be
        completed before the optional &quot;Your Story&quot; section can be viewed.
      </p>

      {completed && (
        <div className="mt-3 text-sm" data-testid={ADMIN.objectiveReview.outcome}>
          <span className="p2p-chip" style={{ background: "#F4F8FB", color: OUTCOME_COLORS[review.outcome] || "#0B2D59", border: `1px solid ${OUTCOME_COLORS[review.outcome] || "#E2E8F0"}` }}>
            {OUTCOME_LABELS[review.outcome] || review.outcome}
          </span>
          <span className="ml-2" style={{ color: "#475569" }}>
            Completed {new Date(review.completed_at).toLocaleString()} by {review.reviewer} · Criteria v{review.criteria_version}
          </span>
        </div>
      )}

      {showForm ? (
        <div className="mt-4 space-y-4">
          {criteria.map((c) => (
            <div key={c.id} className="border-b pb-3" style={{ borderColor: "#E2EBF1" }}>
              <div className="text-sm" style={{ color: "#0B2D59" }}>{c.text}</div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {MARKS.map(([mark, label]) => (
                  <label key={mark} className="flex items-center gap-1.5 text-sm" style={{ color: "#475569" }}>
                    <input type="radio" name={`review-${c.id}`} checked={items[c.id] === mark}
                      onChange={() => setItems((prev) => ({ ...prev, [c.id]: mark }))}
                      data-testid={ADMIN.objectiveReview.mark(c.id, mark)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          ))}
          <textarea className="p2p-input" rows={2} placeholder="Optional objective note (facts only)…"
            data-testid={ADMIN.objectiveReview.note}
            value={note} onChange={(e) => setNote(e.target.value)} />
          <button className="p2p-btn-primary inline-flex items-center gap-2"
            data-testid={ADMIN.objectiveReview.saveBtn}
            onClick={save} disabled={saving || !allMarked}>
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save objective review"}
          </button>
          {!allMarked && <div className="text-xs" style={{ color: "#475569" }}>Mark every criterion to save.</div>}
        </div>
      ) : (
        <ul className="mt-4 space-y-1 text-sm">
          {criteria.map((c) => (
            <li key={c.id} className="flex justify-between gap-3 border-b py-1.5" style={{ borderColor: "#E2EBF1" }}>
              <span style={{ color: "#0B2D59" }}>{c.text}</span>
              <span className="shrink-0 font-medium" style={{ color: OUTCOME_COLORS[review.items?.[c.id] === "does_not_meet" ? "does_not_meet" : "meets"] }}>
                {MARK_LABELS[review.items?.[c.id]] || "—"}
              </span>
            </li>
          ))}
          {review.note && <li className="text-xs pt-1" style={{ color: "#475569" }}>Note: {review.note}</li>}
        </ul>
      )}
    </div>
  );
}

function StoryCard({ doc, data, meta, storyLocked, appId, onChanged }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");
  const [attested, setAttested] = useState(false);
  const [busy, setBusy] = useState(false);

  const review = doc.objective_review || null;
  const reviewDone = !!review?.completed;
  const blocked = reviewDone && review.outcome === "does_not_meet";

  const confirmUnlock = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/applications/${appId}/unlock-story`, {
        attestation: attested,
        unlock_reason: unlockReason,
      });
      toast.success("Story section unlocked");
      setPanelOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unlock");
    } finally { setBusy(false); }
  };

  return (
    <div className="p2p-card p-5" style={{ borderColor: storyLocked ? "#E2E8F0" : "#00A7B5" }}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-serif text-lg flex items-center gap-2" style={{ color: "#0B2D59" }}>
          {storyLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />} Your Story
        </div>
        {storyLocked && !blocked && (
          <button className="p2p-btn-outline text-sm" data-testid={ADMIN.unlockStoryBtn}
            onClick={() => setPanelOpen((o) => !o)} disabled={!reviewDone}
            style={{ padding: "0.4rem 0.9rem", opacity: reviewDone ? 1 : 0.5 }}>
            {reviewDone ? "Unlock…" : "Locked until objective review"}
          </button>
        )}
        {!storyLocked && <span className="p2p-chip" style={{ background: "#00A7B5", color: "#0B2D59" }}>Unlocked</span>}
      </div>

      {storyLocked && blocked && (
        <p className="text-sm mt-3" data-testid={ADMIN.storyUnlock.blockedMsg} style={{ color: "#8A2A2A" }}>
          This section stays locked. The objective review marked this application as not meeting one or
          more written criteria, so the voluntary story section is not part of the review and cannot be opened.
        </p>
      )}

      {storyLocked && !blocked && !panelOpen && (
        <p className="text-sm mt-3" style={{ color: "#475569" }}>
          {reviewDone
            ? "Objective review is complete. Unlocking requires an unlock reason and a fair-use attestation."
            : "Reduce bias: complete the objective criteria review above first. This section stays locked until then."}
        </p>
      )}

      {storyLocked && !blocked && panelOpen && (
        <div className="mt-4 space-y-3 p-4 rounded-lg" data-testid={ADMIN.storyUnlock.panel}
          style={{ background: "#F4F8FB", border: "1px solid #E2EBF1" }}>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#475569" }}>Reason for unlocking</div>
            <select className="p2p-input mt-1" value={unlockReason} onChange={(e) => setUnlockReason(e.target.value)}
              data-testid={ADMIN.storyUnlock.reasonSelect}>
              <option value="">Select a reason…</option>
              {Object.entries(meta?.unlock_reasons || {}).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
          </div>
          <label className="flex items-start gap-2 text-sm" style={{ color: "#0B2D59" }}>
            <input type="checkbox" className="mt-1" checked={attested} onChange={(e) => setAttested(e.target.checked)}
              data-testid={ADMIN.storyUnlock.attestationCheckbox} />
            <span>{meta?.attestation_text ||
              "I understand this section is voluntary and will not be used as a reason to approve, deny, rank, score, or judge the applicant. I will only use it for neutral clarification and respectful communication."}</span>
          </label>
          <button className="p2p-btn-primary inline-flex items-center gap-2" onClick={confirmUnlock}
            data-testid={ADMIN.storyUnlock.confirmBtn}
            disabled={busy || !attested || !unlockReason}>
            <Unlock className="w-4 h-4" /> {busy ? "Unlocking…" : "Unlock story section"}
          </button>
        </div>
      )}

      {!storyLocked && (
        <div className="mt-3 space-y-3">
          {doc.story_unlock && (
            <div className="text-xs" style={{ color: "#475569" }}>
              Unlocked {new Date(doc.story_unlock.ts).toLocaleString()} by {doc.story_unlock.by} — {doc.story_unlock.reason_label}
            </div>
          )}
          {Object.entries(data.story || {}).filter(([k]) => !k.endsWith("_pna") && !k.endsWith("_followup")).map(([k, v]) => (
            <div key={k}>
              <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#475569" }}>{k}</div>
              <div className="text-sm" style={{ color: "#0B2D59" }}>{(data.story || {})[`${k}_pna`] ? <em>Prefer not to answer</em> : (v || <em>—</em>)}</div>
              {(data.story || {})[`${k}_followup`] && (
                <div className="text-sm mt-1 pl-3 border-l-2" style={{ color: "#0B2D59", borderColor: "#00A7B5" }}>
                  Follow-up: {(data.story || {})[`${k}_followup`]}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SupportPlanCard({ appId, plan, onSaved }) {
  const FIELDS = [
    ["communication_preferences", "Communication preferences"],
    ["move_in_support", "Move-in support needs"],
    ["barriers_help", "Barriers the applicant wants help with"],
    ["strengths_supports", "Strengths / supports the applicant identified"],
    ["referrals_resources", "Referrals or resources to offer"],
    ["goals_6_12_months", "Goals the applicant named for six months to one year from now"],
  ];
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (plan) setForm(plan);
  }, [plan]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/applications/${appId}/support-plan`, {
        communication_preferences: form.communication_preferences || "",
        move_in_support: form.move_in_support || "",
        barriers_help: form.barriers_help || "",
        strengths_supports: form.strengths_supports || "",
        referrals_resources: form.referrals_resources || "",
        goals_6_12_months: form.goals_6_12_months || "",
      });
      toast.success("Support plan saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save support plan");
    } finally { setSaving(false); }
  };

  return (
    <div className="p2p-card p-5" data-testid="admin-support-plan-card" style={{ borderColor: "#2A6E4F" }}>
      <div className="font-serif text-lg flex items-center gap-2" style={{ color: "#0B2D59" }}>
        <HeartHandshake className="w-4 h-4" /> Support planning
      </div>
      <p className="text-sm mt-1 p-3 rounded-lg" data-testid="admin-support-plan-disclaimer"
        style={{ background: "#ECF3EC", color: "#2A4535" }}>
        Support planning is not a rental decision tool. It is used only to help provide respectful
        support after the applicant has moved forward in the process.
      </p>
      <div className="mt-4 space-y-3">
        {FIELDS.map(([key, label]) => (
          <div key={key}>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#475569" }}>{label}</label>
            <textarea className="p2p-input mt-1" rows={2}
              data-testid={`admin-support-${key}`}
              value={form[key] || ""}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
          </div>
        ))}
        <button className="p2p-btn-primary inline-flex items-center gap-2"
          data-testid="admin-support-plan-save" onClick={save} disabled={saving}>
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save support plan"}
        </button>
        {plan?.updated_at && (
          <div className="text-xs" style={{ color: "#475569" }}>
            Last updated {new Date(plan.updated_at).toLocaleString()} by {plan.updated_by}
          </div>
        )}
      </div>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="p2p-card p-5">
      <div className="font-serif text-lg mb-3" style={{ color: "#0B2D59" }}>{title}</div>
      {children}
    </div>
  );
}
function Empty({ children }) { return <div className="text-sm" style={{ color: "#475569" }}>{children}</div>; }
function Pairs({ obj }) {
  const entries = Object.entries(obj || {}).filter(([k, v]) => v !== "" && v !== null && v !== undefined);
  if (!entries.length) return <Empty>—</Empty>;
  return (
    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-2">
          <dt className="font-semibold capitalize" style={{ color: "#475569" }}>{k.replaceAll("_", " ")}:</dt>
          <dd style={{ color: "#0B2D59" }}>{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}
function SummaryRow({ label, v }) {
  if (!v) return null;
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#475569" }}>{label}</div>
      <div>{v}</div>
    </div>
  );
}
